import { ColourVariant } from '@weareopenr/component-library';
import { DayOfWeek } from '@weareopenr/types';
import { ITimetableDetail } from 'src/api/getTimetable';
import { getDayLabel } from 'src/helpers/day/day';
import { DAY_OF_WEEK_ORDER, getEntityId, getEntityType, isDefaultSchedule, TimetableEntityType } from './helpers';
import { resolveScheduleColour } from './resolveScheduleColour';

export interface ITimetableEntityGroup {
  entityId: string;
  entityType: TimetableEntityType;
  name: string;
  isDefault: boolean;
  colour: ColourVariant;
  days: DayOfWeek[];
  daysLabel: string;
}

export const formatDaysLabel = (days: DayOfWeek[]): string =>
  DAY_OF_WEEK_ORDER.filter((day) => days.includes(day))
    .map((day) => getDayLabel(day))
    .join(', ');

// One row per unique entity ('menu' | 'priceScheme') in a timetable.
export const groupSchedulesByEntity = (timetable: ITimetableDetail): ITimetableEntityGroup[] => {
  const entitiesByKey = new Map<
    string,
    { group: Omit<ITimetableEntityGroup, 'days' | 'daysLabel'>; days: DayOfWeek[] }
  >();

  timetable.schedules.forEach((schedule) => {
    const entityType = getEntityType(schedule);
    const entityId = getEntityId(schedule);
    const key = `${entityType}:${entityId}`;
    const isDefault = isDefaultSchedule(schedule, timetable);

    const entity = entitiesByKey.get(key) ?? {
      group: {
        entityId,
        entityType,
        name: schedule.name,
        isDefault,
        colour: resolveScheduleColour(schedule.colour_order, isDefault),
      },
      days: [],
    };
    entitiesByKey.set(key, entity);

    if (!entity.days.includes(schedule.day_of_week)) entity.days.push(schedule.day_of_week);
  });

  return Array.from(entitiesByKey.values()).map(({ group, days }) => {
    const sortedDays = DAY_OF_WEEK_ORDER.filter((day) => days.includes(day));
    return { ...group, days: sortedDays, daysLabel: formatDaysLabel(sortedDays) };
  });
};
